import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import { Form, Field } from 'react-final-form';
import { Link } from "react-router-dom";
import { Row, Col, ButtonGroup, Button, Card, CardHeader, CardBody, CardTitle, CardText, CardFooter } from 'reactstrap';
import { FormGroup, Label } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { loadMember, loadGroups, loadUsers, loadMembers } from '../../../redux/modules';
import { Member, Membership, MembershipPayment, User } from '../../../models/';
import { Utils, isSuperAdmin, hasModule } from '../../../services/';
import {
    ParentsField, ParentListItem,
    GroupsField, ClassroomListItem,
    InvoiceDetails, PhoneField,
    FileUpload, Avatar,
} from '../../../components';
import Icon from '@mdi/react';
import {
    mdiAccountCircleOutline,
    mdiMapMarker,
    mdiCardAccountPhoneOutline,
    mdiCheckDecagramOutline,
    mdiAccountCreditCardOutline,
    mdiChairRolling,
    mdiFaceManProfile
} from '@mdi/js';

import { Localization, DatePicker } from 'react-widgets';
import { DateLocalizer } from 'react-widgets/IntlLocalizer';

import './MemberForm.css';

const MemberForm = ({ onSubmit, isCreation }) => {
    const { t }    = useTranslation();
    const dispatch = useDispatch();

    const { users }              = useSelector(state => state.users);
    const { groups }             = useSelector(state => state.groups);
    const { member, members, isLoading, isLoadingOne } = useSelector(state => state.members);

    const [photoInputVisible, setPhotoInputVisible] = useState(false);

    const renderDateTimePicker = ({ input: { onChange, value }, showTime }) =>
        <Localization
            messages={{
                moveToday: t('common.today'),
                moveBack: t('common.move_back'),
                moveForward: t('common.move_forward'),
            }}
        >
            <DatePicker
                onChange={onChange}
                format="DD MMM YYYY"
                parse={['D M Y', 'DD MM YYYY']}
                valueEditFormat={{ dateStyle: "short" }}
                valueDisplayFormat={{ dateStyle: "medium" }}
                includeTime={showTime}
                value={!value ? null : new Date(value)}
            />
        </Localization>

    useEffect(() => {
        dispatch(loadMembers());
        dispatch(loadUsers());
        dispatch(loadGroups());
    }, []);

    const reloadMember = () => {
        dispatch(loadMember(member.id));
    };

    return (
        <Form
            initialValues={ member }
            onSubmit={ values => { onSubmit(values); }}
            render={({ handleSubmit, pristine, form, submitting }) => (
                <form className="form" onSubmit={handleSubmit}>
                    {!isCreation && (
                    <Row className="main row-flex">
                        <Col sm={12}>
                            <FormGroup row className="justify-content-center">
                                <Col sm={3}>
                                    <div style={{maxWidth: '350px', cursor: 'pointer'}} class="flex text-center">
                                        {member?.photo ? (
                                            <Avatar
                                                width={120}
                                                className="d-block mx-auto"
                                                src={`${member.photo}?t=${Date.now()}`}$
                                                onClick={() => setPhotoInputVisible(!photoInputVisible)}
                                            />
                                        ) : (
                                            <Icon
                                                className="d-block mx-auto"
                                                path={mdiFaceManProfile}
                                                size={5}
                                                onClick={() => setPhotoInputVisible(!photoInputVisible)}
                                            />
                                        )}
                                        {photoInputVisible && (
                                        <div className="d-flex flex-row">
                                            <Field
                                                component={FileUpload}
                                                className="form-control"
                                                name="new_photo"
                                            />
                                            <Button type="submit" color="success" disabled={pristine || submitting || isLoading}>
                                                <span>
                                                    <i className={`fa ${ isLoading ? 'fa-spinner fa-spin' : 'fa fa-save'}`}></i>
                                                </span>
                                            </Button>
                                        </div>
                                        )}
                                    </div>
                                </Col>
                            </FormGroup>
                        </Col>
                    </Row>
                    )}
                    <Row className="main row-flex">
                        <Col sm={12} lg={6}>
                            <Card color="light" className="h-100">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiAccountCircleOutline} size={1.5} />&nbsp;
                                        {t('members.profile')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <CardText tag="div">
                                        <FormGroup row>
                                            <Label for="number" sm={3}>{t('members.number')}</Label>
                                            <Col sm={9}>
                                                <Field disabled component="input" className="form-control" type="text" name="number" id="number" />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="firstname" sm={3}>{t('members.firstname')}</Label>
                                            <Col sm={9}>
                                                <Field required component="input" className="form-control" type="text" name="firstname" id="firstname" autoComplete="given-name" placeholder={t('members.firstname_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="lastname" sm={3}>{t('members.lastname')}</Label>
                                            <Col sm={9}>
                                                <Field required component="input" className="form-control" type="text" name="lastname" id="lastname" autoComplete="family-name" placeholder={t('members.lastname_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="gender" sm={3}>{t('members.gender')}</Label>
                                            <Col sm={9}>
                                                <Field required component="select" className="form-control" type="text" name="gender" id="gender">
                                                    {
                                                        User.genders().map(g => <option key={g} value={g}>{g}</option>)
                                                    }
                                                </Field>
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="birthdate" sm={3}>{t('members.birthdate')}</Label>
                                            <Col sm={9}>
                                                <Field showTime={false} component={renderDateTimePicker} className="form-control" type="text" name="birthdate" id="birthdate" autoComplete="" />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="birthplace" sm={3}>{t('members.birthplace')}</Label>
                                            <Col sm={9}>
                                                <Field component="input" className="form-control" type="text" name="birthplace" id="birthplace" />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="nationality" sm={3}>{t('members.nationality')}</Label>
                                            <Col sm={9}>
                                                <Field component="input" className="form-control" type="text" name="nationality" id="nationality" />
                                            </Col>
                                        </FormGroup>
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col sm={12} lg={6}>
                            <Card color="light" className="h-100">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiMapMarker} size={1.5} />
                                        {t('members.address')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <CardText>
                                        <FormGroup row>
                                            <Label for="address" sm={3}>{t('members.address')}</Label>
                                            <Col sm={9}>
                                                <Field component="input" className="form-control" type="text" name="address" />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="zip" sm={3}>{t('members.zip')}</Label>
                                            <Col sm={9}>
                                                <Field component="input" className="form-control" type="text" name="zip" />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="city" sm={3}>{t('members.city')}</Label>
                                            <Col sm={9}>
                                                <Field component="input" className="form-control" type="text" name="city" />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="country" sm={3}>{t('members.country')}</Label>
                                            <Col sm={9}>
                                                <Field component="input" className="form-control" type="text" name="country" />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="remarks" sm={3}>{t('members.remarks')}</Label>
                                            <Col sm={9}>
                                                <Field component="textarea" rows={ 6 } className="form-control" name="remarks" id="remarks" />
                                            </Col>
                                        </FormGroup>
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    <Row className="main row-flex">
                        <Col sm={12} lg={6}>
                            <Card color="light" className="h-100">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiCardAccountPhoneOutline} size={1.5} />&nbsp;
                                        {t('members.contact')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <CardText>
                                        <FormGroup row>
                                            <Label for="email" sm={3}>{t('members.email')}</Label>
                                            <Col sm={9}>
                                                <Field required component="input" className="form-control" type="text" name="email" />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="cell_phone" sm={3}>{t('members.cell_phone')}</Label>
                                            <Col sm={9}>
                                                <PhoneField className="form-control"  name="cell_phone" placeholder={t('members.cell_phone_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="fix_phone" sm={3}>{t('members.fix_phone')}</Label>
                                            <Col sm={9}>
                                                <Field component="input" className="form-control" type="text" name="fix_phone" />
                                            </Col>
                                        </FormGroup>
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                        { !isCreation && (
                        <Col sm={12} lg={6}>
                            <Card color="light" className="h-100">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiCheckDecagramOutline} size={1.5} />&nbsp;
                                        {t('members.membership')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    { !isCreation && (
                                    <CardText>
                                        <FormGroup row>
                                            <Label for="membership.status" sm={3}>{t('members.membership_status')}</Label>
                                            <Col sm={9}>
                                                <Field required component="select" className="form-control" type="text" name="membership.status" id="membership.status">
                                                    {
                                                        Membership.status().map(s => <option key={s} value={s}>{ t(`member_memberships.status_${s}`) }</option>)
                                                    }
                                                </Field>
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="membership.mode" sm={3}>{t('membership_payments.mode')}</Label>
                                            <Col sm={9}>
                                                <Field required component="select" className="form-control" type="text" name="membership.mode">
                                                    {
                                                        MembershipPayment.modes().map(m => <option key={m} value={m}>{ t(`membership_payments.mode_${m}`) }</option>)
                                                    }
                                                </Field>
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="membership.status" sm={3}>{t('member_memberships.frequency')}</Label>
                                            <Col sm={9}>
                                                <Field required component="select" className="form-control" type="text" name="membership.payment_frequency">
                                                    {
                                                        Membership.frequencies().map(f => <option key={f} value={f}>{ t(`member_memberships.frequency_${f}`) }</option>)
                                                    }
                                                </Field>
                                            </Col>
                                        </FormGroup>
                                    </CardText>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                        )}
                    </Row>
                    <Row className="main row-flex">
                        <Col sm={12} lg={6}>
                            <Card color="light" className="h-100">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiChairRolling} size={1.5} />&nbsp;
                                        {t('members.administration')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <CardText>
                                        <FormGroup row>
                                            <Label for="in_directors" sm={3}>{t('members.in_directors')}</Label>
                                            <Col sm={9}>
                                                <Field component="select" className="form-control" type="text" name="in_directors" id="members.in_directors">
                                                    <option value={false}>{ t('common.no') }</option>)
                                                    <option value={true}>{ t('common.yes') }</option>)
                                                </Field>
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="in_office" sm={3}>{t('members.in_office')}</Label>
                                            <Col sm={9}>
                                                <Field component="select" className="form-control" type="text" name="in_office" id="members.in_office">
                                                    <option value={false}>{ t('common.no') }</option>)
                                                    <option value={true}>{ t('common.yes') }</option>)
                                                </Field>
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="position" sm={3}>{t('members.position')}</Label>
                                            <Col sm={9}>
                                                <Field component="input" className="form-control" type="text" name="position" id="position" />
                                            </Col>
                                        </FormGroup>
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                        { !isCreation && (
                        <Col sm={12} lg={6}>
                            <Card color="light" className="h-100">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiAccountCreditCardOutline} size={1.5} />&nbsp;
                                        {t('members.membership_fees')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <CardText tag="div">
                                        <CardTitle tag="h3">
                                            { t('members.invoices') }
                                        </CardTitle>
                                        <CardTitle tag="h5">
                                            { !member?.membership?.invoices?.length && t('members.has_no_invoice') }
                                        </CardTitle>
                                        { member?.membership?.invoices?.map((invoice, i) => (
                                            <InvoiceDetails
                                                isMembership
                                                paymentName="new_payment"
                                                invoice={invoice}
                                                className="h-100"
                                                pristine={pristine}
                                                submitting={submitting}
                                                isLoadingOne={isLoadingOne}
                                                onPaymentDeleted={reloadMember}
                                            />
                                        ))}
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                        )}
                    </Row>

                    <Row className="main">
                        <Col className="text-center">
                            <ButtonGroup>
                                <Button tag={Link} to={ -1 } color="warning">
                                    <span><i className="fa fa-arrow-left"></i>&nbsp;{t('common.back')}</span>
                                </Button>
                                <Button type="submit" color="success" disabled={pristine || submitting || isLoading}>
                                    <span>
                                        <i className={`fa ${ isLoading ? 'fa-spinner fa-spin' : 'fa fa-save'}`}></i>&nbsp;{t("common.save")}
                                    </span>
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Row>
                </form>
        )} />
    );
}

export default MemberForm;
